import { Button, Card, Col, Divider, Row, Skeleton, Switch, Tag, notification } from "antd";
import { useCallback, useEffect, useState } from "react";
import {
  apiAccountAcquirerPriority,
  apiUpdateAccountAcquirerPriority,
} from "../../../../../api/accounts";
import AcquirerPriorityList from "../../../../common/AcquirerPriorityList/AcquirerPriorityList";
import "./AcquirerPriority.scss";

const AcquirerPriority = ({ data }) => {
  const accountId = data?.id;

  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [acquirers, setAcquirers] = useState([]);
  const [systemEntries, setSystemEntries] = useState({});
  const [entries, setEntries] = useState({});
  const [registered, setRegistered] = useState([]);
  const [prefer3ds, setPrefer3ds] = useState(false);
  const [systemPrefer3ds, setSystemPrefer3ds] = useState(false);

  const acquirerLabels = Object.fromEntries(acquirers.map((item) => [item.code, item.label]));

  const fetchPriority = useCallback(async () => {
    if (!accountId) {
      return;
    }
    setLoading(true);
    setLoaded(false);
    try {
      const result = await apiAccountAcquirerPriority(accountId);
      setPaymentMethods(result.payment_methods || []);
      setAcquirers(result.acquirers || []);
      setSystemEntries(result.system_entries || {});
      setEntries(result.entries || {});
      setRegistered(result.registered_acquirers || []);
      setPrefer3ds(Boolean(result.prefer_3ds));
      setSystemPrefer3ds(Boolean(result.system_prefer_3ds));
      setLoaded(true);
    } catch (error) {
      notification.error({
        message: "Erro",
        description: "Erro ao carregar a prioridade de adquirentes.",
        placement: "topRight",
      });
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    fetchPriority();
  }, [fetchPriority]);

  const handleToggleDefault = (paymentMethod, useDefault) => {
    setEntries((current) => {
      const next = { ...current };
      if (useDefault) {
        delete next[paymentMethod];
      } else {
        next[paymentMethod] = [...(systemEntries[paymentMethod] || [])];
      }
      return next;
    });
  };

  const handleReorder = (paymentMethod, order) => {
    setEntries((current) => ({ ...current, [paymentMethod]: order }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await apiUpdateAccountAcquirerPriority(accountId, {
        entries,
        prefer_3ds: prefer3ds,
      });
      setEntries(result.entries || {});
      setSystemEntries(result.system_entries || {});
      setPrefer3ds(Boolean(result.prefer_3ds));
      setSystemPrefer3ds(Boolean(result.system_prefer_3ds));
      notification.success({
        message: "Sucesso",
        description: "Prioridade de adquirentes salva.",
        placement: "topRight",
      });
    } catch (error) {
      notification.error({
        message: "Erro",
        description: "Erro ao salvar a prioridade de adquirentes.",
        placement: "topRight",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Skeleton active />;
  }

  return (
    <div className="AcquirerPriority">
      <h3 className="subtitle-page">Prioridade de adquirentes</h3>
      <Divider />
      <p className="priority-hint">
        Por padrão este vendedor segue a prioridade definida nas configurações do sistema. Desative
        "Usar padrão do sistema" para definir uma ordem específica para um meio de pagamento.
      </p>
      <div className="priority-registered">
        <span className="priority-registered-label">Cadastrado em:</span>
        {acquirers.map((acquirer) => (
          <Tag key={acquirer.code} color={registered.includes(acquirer.code) ? "green" : "default"}>
            {registered.includes(acquirer.code)
              ? acquirer.label
              : `${acquirer.label} (sem cadastro)`}
          </Tag>
        ))}
      </div>
      <Card size="small" className="priority-3ds">
        <div className="priority-3ds-row">
          <div>
            <div className="priority-3ds-title">Preferir 3DS</div>
            <div className="priority-3ds-hint">
              {systemPrefer3ds
                ? "O 3DS está ativado nas configurações do sistema e vale para todos os vendedores."
                : "Ativa a autenticação 3D Secure nos pagamentos com cartão de crédito deste vendedor. Adquirentes sem suporte a 3DS seguem o fluxo normal."}
            </div>
          </div>
          <Switch
            checked={systemPrefer3ds || prefer3ds}
            disabled={systemPrefer3ds || saving}
            onChange={setPrefer3ds}
          />
        </div>
      </Card>
      <Row gutter={[16, 16]}>
        {paymentMethods.map((method) => {
          const useDefault = !entries[method.code];
          return (
            <Col key={method.code} xs={24} md={12} xl={8}>
              <Card
                size="small"
                title={method.label}
                extra={
                  <span className="priority-switch">
                    <span className="priority-switch-label">Usar padrão do sistema</span>
                    <Switch
                      size="small"
                      checked={useDefault}
                      onChange={(checked) => handleToggleDefault(method.code, checked)}
                    />
                  </span>
                }
              >
                <AcquirerPriorityList
                  value={useDefault ? systemEntries[method.code] || [] : entries[method.code]}
                  labels={acquirerLabels}
                  disabled={useDefault || saving}
                  onChange={(order) => handleReorder(method.code, order)}
                />
              </Card>
            </Col>
          );
        })}
      </Row>
      {loaded && (
        <div className="priority-actions">
          <Button type="primary" loading={saving} onClick={handleSave}>
            Salvar
          </Button>
        </div>
      )}
    </div>
  );
};

export default AcquirerPriority;
